import React from 'react';
import {Input, InputType, IInputProps} from '~/components/Controls/Input';
import {FormField, hasError, IFieldProps} from '~/components/Form';
import {Intent} from '~/lib/intent';

const normalizeNumericInput = (value: string): string => {
    const withoutSpaces = value.replace(/[\s\u00A0\u202F]/g, '');
    const stripped = withoutSpaces.replace(/[^\d.,]/g, '');
    const lastCommaIndex = stripped.lastIndexOf(',');
    const lastDotIndex = stripped.lastIndexOf('.');
    const separatorIndex = Math.max(lastCommaIndex, lastDotIndex);
    if (separatorIndex === -1) {
        return stripped.replace(/[^\d]/g, '');
    }
    const integerPart = stripped.slice(0, separatorIndex).replace(/[^\d]/g, '');
    const decimalPart = stripped.slice(separatorIndex + 1).replace(/[^\d]/g, '');
    if (!decimalPart) {
        return `${integerPart}.`;
    }
    return `${integerPart}.${decimalPart}`;
};

const formatWithThousands = (value: string): string => {
    if (!value) {
        return '';
    }
    const hasDot = value.includes('.');
    const [integerPartRaw = '', decimalPartRaw = ''] = value.split('.');
    const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '');
    const formattedInteger = (integerPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    if (hasDot) {
        return `${formattedInteger},${decimalPartRaw}`;
    }
    return formattedInteger;
};

const getDisplayValue = (rawValue: unknown): string => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
        return '';
    }
    if (typeof rawValue === 'number') {
        return formatWithThousands(String(rawValue));
    }
    if (typeof rawValue === 'string') {
        return formatWithThousands(normalizeNumericInput(rawValue));
    }
    return '';
};

const countNonSpace = (str: string): number =>
    str.replace(/[\s\u00A0\u202F]/g, '').length;

const findCursorPosition = (formatted: string, nonSpaceChars: number): number => {
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
        if (count === nonSpaceChars) {
            return i;
        }
        if (!/[\s\u00A0\u202F]/.test(formatted[i])) {
            count++;
        }
    }
    return formatted.length;
};

export const UsdInputField = (props: IFieldProps<IInputProps>) => {
    const currentValue = props.field.value;
    const [localDisplay, setLocalDisplay] = React.useState(() => getDisplayValue(currentValue));
    const inputRef = React.useRef<HTMLInputElement>(null);
    const cursorPos = React.useRef<number | null>(null);

    React.useEffect(() => {
        const localNormalized = normalizeNumericInput(localDisplay);
        const localNumeric = localNormalized ? Number(localNormalized) : null;
        const isInSync = typeof currentValue === 'number'
            ? localNumeric === currentValue
            : !currentValue && !localNumeric;

        if (!isInSync) {
            setLocalDisplay(getDisplayValue(currentValue));
        }
    }, [currentValue]);

    React.useLayoutEffect(() => {
        if (cursorPos.current !== null && inputRef.current) {
            inputRef.current.setSelectionRange(cursorPos.current, cursorPos.current);
            cursorPos.current = null;
        }
    });

    return (
        <FormField {...props}>
            <Input
                ref={inputRef}
                {...props.options}
                type={InputType.STRING}
                value={localDisplay}
                intent={hasError(props) && Intent.DANGER}
                onBlur={() => setLocalDisplay(getDisplayValue(currentValue))}
                onChange={value => {
                    const rawCursor = inputRef.current?.selectionStart ?? null;
                    const normalized = normalizeNumericInput(value);
                    const formatted = formatWithThousands(normalized);

                    if (rawCursor !== null) {
                        const charsBeforeCursor = countNonSpace(value.slice(0, rawCursor));
                        cursorPos.current = findCursorPosition(formatted, charsBeforeCursor);
                    }

                    setLocalDisplay(formatted);

                    if (!normalized || normalized === '.') {
                        props.form.setFieldValue(props.field.name, '');
                        return;
                    }

                    const numeric = Number(normalized);
                    if (!Number.isFinite(numeric)) {
                        props.form.setFieldValue(props.field.name, '');
                        return;
                    }

                    props.form.setFieldValue(props.field.name, numeric);
                }}
            />
        </FormField>
    );
};